import axios, { type AxiosProgressEvent } from 'axios';

import { type Common } from './common.js';
import type * as InsuranceCardOcrResult from './models/insuranceOcrResult.js';
import {
  type DocumentType,
  type FindAllOptions,
  type PatientUpload,
  type SignedUploadURLResult,
} from './models/patientUpload.js';

export class API {
  common: Common;
  baseEndpoint = '/patient-uploads';

  constructor(common: Common) {
    this.common = common;
  }

  snakeCaseToKebab(snake: string): string {
    return snake.replace(/_/g, '-').toLowerCase();
  }

  getDownloadFileById(patientUploadId: string): string {
    return this.common.constructPath(`${this.baseEndpoint}/${patientUploadId}/file`);
  }

  async findAll(options?: FindAllOptions): Promise<PatientUpload[]> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllWithDocumentTypeByEncounterId(
    encounterId: string,
    documentType: DocumentType
  ): Promise<PatientUpload[]> {
    const url = this.common.constructPath(
      `/patient-previsit/patient-uploads/${encounterId}/${this.snakeCaseToKebab(documentType)}`
    );
    const { results } = await this.common.getJSON(url);
    return results;
  }

  async findOneByType(
    encounterId: string,
    documentType: DocumentType
  ): Promise<PatientUpload | undefined> {
    const url = this.common.constructPath(
      `/patient-previsit/patient-uploads/${encounterId}/${this.snakeCaseToKebab(documentType)}`
    );
    const { results } = await this.common.getJSON(url);
    return results[0];
  }

  async getSignedUploadURL(
    encounterId: string,
    documentType: DocumentType,
    options: {
      insuranceProviderName?: string;
      primary?: boolean;
      patientInsuranceInformationId?: string;
    } = {}
  ): Promise<SignedUploadURLResult> {
    const url = this.common.constructPath(
      `/patient-previsit/patient-uploads/${encounterId}/${this.snakeCaseToKebab(documentType)}`
    );
    return await this.common.postJSON(url, options);
  }

  async getSignedUploadURLByFormSetId(options: {
    formSetId: string;
    documentRoute: DocumentType;
    patientInsuranceInformationId?: string;
    insuranceProviderName?: string;
    primary?: boolean;
  }): Promise<SignedUploadURLResult> {
    const formattedDocumentType = this.snakeCaseToKebab(options.documentRoute);
    const url = this.common.constructPath('/patient-previsit/patient-uploads/form-set');
    return await this.common.postJSON(url, {
      ...options,
      documentRoute: formattedDocumentType,
    });
  }

  async upload(
    file: Blob,
    signedUploadURL: string,
    onUploadProgress: (event: AxiosProgressEvent) => void
  ): Promise<number> {
    const response = await axios.put(signedUploadURL, file, {
      onUploadProgress,
      headers: {
        'Content-Type': 'image/jpg',
      },
      timeout: 30000,
    });
    return response.status;
  }

  async validateUploadedDocuments(id: string): Promise<number> {
    const url = this.common.constructPath(`/patient-previsit/patient-uploads/${id}/scan`);
    return await this.common.postJSON(url, {});
  }

  async downloadImageFile(id: string): Promise<{ status: number; dataURL: string }> {
    const url = this.common.constructPath(`/patient-previsit/patient-uploads/${id}/file`);
    return await this.common.getBlob(url, {
      headers: { 'cache-control': 'no-cache' },
    });
  }

  async invalidateCurrentDocument(id: string): Promise<number> {
    const url = this.common.constructPath(`/patient-previsit/patient-uploads/${id}/file`);
    return await this.common.deleteJSON(url);
  }

  async recover(id: string): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`/patient-previsit/patient-uploads/${id}`);
    return await this.common.patchJSON(url, {
      isDeleted: false,
      deletedTimestamp: null,
    });
  }

  async ocrInsuranceCard(
    patientUploadId: string,
    side: 'FRONT' | 'BACK',
    payor?: string
  ): Promise<InsuranceCardOcrResult.InsuranceCardOcrResult> {
    const url = this.common.constructPath(
      `/patient-previsit/patient-uploads/${patientUploadId}/ocr`
    );
    return await this.common.postJSON(url, { side, ...(payor && { payor }) });
  }
}
